"use client";

import { useState, useTransition } from "react";
import { ChevronDown, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  CREATE_VENUES_FOR_STAFF,
  GLOBAL_FEATURES,
  VENUE_FEATURES,
  type GlobalFeature,
  type VenueFeature,
} from "@/lib/constants/features";
import { saveUserFeatures } from "@/app/superadmin/users/actions";

type Venue = { id: number; name: string; features: string[] };

const VENUE_FEATURE_KEYS = new Set<string>(VENUE_FEATURES.map((f) => f.key));

/**
 * What a booking staff member can do at each of their venues: one card per
 * venue with that venue's features, plus features that apply across all
 * venues. Venues themselves are managed in Edit user.
 *
 * Cards, not one features x venues table: a table gets crowded and hard to
 * read once a user has several venues (UX decision, see
 * doc/user-permissions-plan.md section 8).
 *
 * Saved to user_venue_permissions.features. Features across all venues are
 * kept in the same list on each venue row; only Create venues can be given
 * so far, the others stay super admin only.
 */
const GRANTABLE_GLOBAL = [
  "venues_admin",
] as const satisfies readonly GlobalFeature[];
type GrantableGlobal = (typeof GRANTABLE_GLOBAL)[number];
const isGrantable = (f: string): f is GrantableGlobal =>
  (GRANTABLE_GLOBAL as readonly string[]).includes(f);

type SaveAccess = (input: {
  userInfoId: number;
  venues: Record<number, VenueFeature[]>;
  global: GrantableGlobal[];
}) => Promise<{ success: boolean; error?: string }>;

export type FeatureAccessCardProps = {
  userInfoId: number;
  venues: Venue[];
  globalFeatures?: string[];
  /** Whether a feature can be switched at a venue; the rest show locked. All by default. */
  canSwitch?: (venueId: number, feature: VenueFeature) => boolean;
  /** Show the "Across all venues" features. */
  showGlobal?: boolean;
  /** Saves the access; the super admin action when left out. */
  onSave?: SaveAccess;
  description?: string;
};

/**
 * A permission's label and switch sit far apart on wide screens, so the
 * whole row highlights on hover to show which switch belongs to which label,
 * and clicking anywhere on the label toggles it. Rows that cannot be changed
 * stay still.
 */
const permissionRow = (changeable: boolean) =>
  `flex items-start justify-between gap-3 rounded-md px-2 py-2 transition-colors ${
    changeable ? "hover:bg-muted/70" : ""
  }`;

const permissionLabel = (changeable: boolean) =>
  `flex-1 text-sm leading-tight ${changeable ? "cursor-pointer" : "cursor-default"}`;

export function FeatureAccessCard({
  userInfoId,
  venues,
  globalFeatures = [],
  canSwitch = () => true,
  // "Across all venues" only offers Create venues, which is hidden for now.
  showGlobal = CREATE_VENUES_FOR_STAFF,
  onSave = saveUserFeatures,
  description = "What this person can do at each venue. Venues are added or removed in Edit user.",
}: FeatureAccessCardProps) {
  const initial = () =>
    Object.fromEntries(
      venues.map((v) => [
        v.id,
        v.features.filter((f): f is VenueFeature => VENUE_FEATURE_KEYS.has(f)),
      ]),
    ) as Record<number, VenueFeature[]>;

  const initialGlobal = (): GrantableGlobal[] =>
    globalFeatures.filter(isGrantable);

  const [grants, setGrants] = useState(initial);
  const [saved, setSaved] = useState(initial);
  const [globalGrants, setGlobalGrants] = useState(initialGlobal);
  const [savedGlobal, setSavedGlobal] = useState(initialGlobal);
  const [saving, startSaving] = useTransition();
  // A single venue starts open; with several, all start collapsed so the
  // page stays short - each header still shows how many features are on.
  const [openVenues, setOpenVenues] = useState<Set<number>>(
    () => new Set(venues.length === 1 ? [venues[0].id] : []),
  );

  const setVenueOpen = (venueId: number, open: boolean) =>
    setOpenVenues((prev) => {
      const next = new Set(prev);
      if (open) next.add(venueId);
      else next.delete(venueId);
      return next;
    });

  const has = (venueId: number, feature: VenueFeature) =>
    grants[venueId]?.includes(feature) ?? false;

  const toggle = (venueId: number, feature: VenueFeature) =>
    setGrants((prev) => {
      const current = prev[venueId] ?? [];
      return {
        ...prev,
        [venueId]: current.includes(feature)
          ? current.filter((f) => f !== feature)
          : [...current, feature],
      };
    });

  /** Every feature that can be switched at one venue, on or off. */
  const setVenue = (venueId: number, on: boolean) =>
    setGrants((prev) => {
      const locked = (prev[venueId] ?? []).filter(
        (f) => !canSwitch(venueId, f),
      );
      const switched = on
        ? VENUE_FEATURES.map((f) => f.key).filter((f) => canSwitch(venueId, f))
        : [];
      return { ...prev, [venueId]: [...locked, ...switched] };
    });

  const sorted = (g: Record<number, VenueFeature[]>) =>
    JSON.stringify(
      Object.entries(g).map(([id, features]) => [id, [...features].sort()]),
    );
  const dirty =
    sorted(grants) !== sorted(saved) ||
    [...globalGrants].sort().join() !== [...savedGlobal].sort().join();

  const toggleGlobal = (feature: GrantableGlobal) =>
    setGlobalGrants((prev) =>
      prev.includes(feature)
        ? prev.filter((f) => f !== feature)
        : [...prev, feature],
    );

  const save = () =>
    startSaving(async () => {
      const result = await onSave({
        userInfoId,
        venues: grants,
        global: globalGrants,
      });
      if (!result.success) {
        toast.error(result.error ?? "Could not save access");
        return;
      }
      setSaved(grants);
      setSavedGlobal(globalGrants);
      toast.success("Access saved");
    });

  return (
    <div className="bg-card border rounded-lg p-6 mb-6">
      <div className="mb-1 flex items-center justify-between gap-4">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          Access
        </h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!dirty || saving}
            onClick={() => {
              setGrants(saved);
              setGlobalGrants(savedGlobal);
            }}
          >
            Discard
          </Button>
          <Button size="sm" disabled={!dirty || saving} onClick={save}>
            {saving ? "Saving..." : "Save access"}
          </Button>
        </div>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">{description}</p>

      {venues.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No venues yet - add one in Edit user first.
        </p>
      ) : (
        <div className="space-y-4">
          {venues.map((v) => {
            const granted = VENUE_FEATURES.filter((f) => has(v.id, f.key));
            const all = VENUE_FEATURES.filter((f) =>
              canSwitch(v.id, f.key),
            ).every((f) => has(v.id, f.key));
            const open = openVenues.has(v.id);
            return (
              <Collapsible
                key={v.id}
                open={open}
                onOpenChange={(next) => setVenueOpen(v.id, next)}
                asChild
              >
                <section
                  aria-labelledby={`venue-${v.id}-title`}
                  className="rounded-md border"
                >
                  <div
                    className={`flex items-center justify-between gap-4 bg-muted/50 px-4 py-3 ${open ? "border-b" : ""}`}
                  >
                    <CollapsibleTrigger className="group flex min-w-0 flex-1 items-center gap-2 text-left">
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90" />
                      <div className="min-w-0">
                        <h3
                          id={`venue-${v.id}-title`}
                          className="truncate font-medium"
                        >
                          {v.name}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {granted.length} of {VENUE_FEATURES.length} features
                        </p>
                      </div>
                    </CollapsibleTrigger>
                    <button
                      type="button"
                      onClick={() => setVenue(v.id, !all)}
                      className="shrink-0 text-xs text-primary hover:underline"
                    >
                      {all ? "Clear all" : "Select all"}
                    </button>
                  </div>
                  <CollapsibleContent className="grid grid-cols-1 gap-1 p-2">
                    {VENUE_FEATURES.map((feature) => {
                      const id = `venue-${v.id}-${feature.key}`;
                      return (
                        <div
                          key={feature.key}
                          className={permissionRow(
                            canSwitch(v.id, feature.key),
                          )}
                        >
                          <label
                            htmlFor={id}
                            className={permissionLabel(
                              canSwitch(v.id, feature.key),
                            )}
                          >
                            {feature.label}
                            <span className="block text-xs text-muted-foreground">
                              {canSwitch(v.id, feature.key)
                                ? feature.hint
                                : "You can't change this one"}
                            </span>
                          </label>
                          <Switch
                            id={id}
                            checked={has(v.id, feature.key)}
                            disabled={!canSwitch(v.id, feature.key)}
                            onCheckedChange={() => toggle(v.id, feature.key)}
                          />
                        </div>
                      );
                    })}
                  </CollapsibleContent>
                </section>
              </Collapsible>
            );
          })}
        </div>
      )}

      {showGlobal && (
        <>
          <h3 className="mb-2 mt-6 text-sm font-medium">Across all venues</h3>
          <div className="grid grid-cols-1 gap-1 rounded-md border p-2">
            {GLOBAL_FEATURES.map((feature) => {
              const id = `global-${feature.key}`;
              return (
                <div
                  key={feature.key}
                  className={permissionRow(isGrantable(feature.key))}
                >
                  <label
                    htmlFor={id}
                    className={permissionLabel(isGrantable(feature.key))}
                  >
                    {feature.label}
                    <span className="block text-xs text-muted-foreground">
                      {feature.hint}
                    </span>
                  </label>
                  {isGrantable(feature.key) ? (
                    <Switch
                      id={id}
                      checked={globalGrants.includes(feature.key)}
                      onCheckedChange={() =>
                        toggleGlobal(feature.key as GrantableGlobal)
                      }
                    />
                  ) : (
                    <Switch id={id} checked={false} disabled />
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-4 text-xs text-muted-foreground">
            Across all venues: only Create venues can be given so far; a venue
            they create is added to their venues with the default features. A
            venue added in Edit user starts with no features. Turning a feature
            off hides its menu item and page in the booking portal at that
            venue.
          </p>
        </>
      )}
    </div>
  );
}
